import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Flight, GameState, Round } from '@flugwettrennen/shared';
import { api, ApiError } from '../api';
import { ChallengeBadge } from '../components/ChallengeBadge';
import { DepartureBoard, type MoreMode } from '../components/DepartureBoard';
import { FlightDetails } from '../components/FlightDetails';
import { FlightTime } from '../components/FlightTime';
import { GoalProgress } from '../components/GoalProgress';
import { RouteMap } from '../components/RouteMap';
import { hhmm } from '../lib/format';
import { goalLabel } from '../lib/goals';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Wie viele Flüge die Tafel auf einmal zeigt bzw. je Klick aufklappt. */
const BOARD_PAGE_SIZE = 15;

export function GamePage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [round, setRound] = useState<Round | null>(null);
  const [gstate, setGstate] = useState<GameState | null>(null);
  const [selectedFlight, setSelectedFlight] = useState<Flight | null>(null);
  const [flights, setFlights] = useState<Flight[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rolling, setRolling] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'win' | 'lose' | 'error'; text: string } | null>(null);
  const [waiting, setWaiting] = useState<string | null>(null);
  const [detailFlight, setDetailFlight] = useState<Flight | null>(null);
  const [visibleCount, setVisibleCount] = useState(BOARD_PAGE_SIZE);
  const [windows, setWindows] = useState(1);
  const [maxWindows, setMaxWindows] = useState(1);
  const [moreBusy, setMoreBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await api.rounds.get(id);
    setRound(r.round);
    setGstate(r.state);
    setSelectedFlight(r.selectedFlight);
    if (r.state.phase === 'finished') {
      navigate(`/result/${id}`);
      return;
    }
    if (r.state.phase === 'in_flight') {
      navigate(`/round/${id}/live`);
      return;
    }
    if (r.state.phase === 'boarding') {
      const d = await api.game.departures(id);
      setFlights(d.flights);
      setWindows(d.windows);
      setMaxWindows(d.maxWindows);
      setVisibleCount(BOARD_PAGE_SIZE);
    }
  }, [id, navigate]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Im Live-Modus läuft die Uhr draußen weiter: der Wechsel in den Flug gehört
   * an die Abflugzeit, nicht an einen Klick. Deshalb fragen wir regelmäßig nach
   * – der Server vollzieht den Übergang, sobald die echte Zeit erreicht ist,
   * und liefert dabei den aktuellen Stand des Fluges samt Verspätung zurück.
   */
  useEffect(() => {
    if (round?.mode !== 'live' || gstate?.phase !== 'selected') return;
    let stopped = false;

    async function poll() {
      try {
        const res = await api.game.advance(id, true);
        if (stopped) return;
        if (res.selectedFlight) setSelectedFlight(res.selectedFlight);
        setWaiting(res.waitingUntil);
        if (res.state.phase === 'in_flight') {
          navigate(`/round/${id}/live`);
          return;
        }
        setGstate(res.state);
      } catch {
        // Netzwerkaussetzer übergehen, der nächste Durchlauf versucht es erneut.
      }
    }

    void poll();
    const iv = setInterval(() => void poll(), 30000);
    return () => {
      stopped = true;
      clearInterval(iv);
    };
  }, [id, navigate, round?.mode, gstate?.phase]);

  /**
   * Erst werden die schon geladenen Flüge aufgeklappt – das kostet nichts.
   * Ist die Tafel zu Ende, holt der nächste Klick weitere zwölf Stunden, und
   * das ist eine echte Anfrage beim Datenanbieter.
   */
  const moreMode: MoreMode =
    visibleCount < flights.length ? 'expand' : windows < maxWindows ? 'load' : null;

  async function showMore() {
    if (visibleCount < flights.length) {
      setVisibleCount((v) => v + BOARD_PAGE_SIZE);
      return;
    }
    if (windows >= maxWindows || moreBusy) return;
    setMoreBusy(true);
    try {
      const d = await api.game.departures(id, windows + 1);
      setFlights(d.flights);
      setWindows(d.windows);
      setMaxWindows(d.maxWindows);
      setVisibleCount((v) => v + BOARD_PAGE_SIZE);
    } catch {
      setMsg({ kind: 'error', text: t('errors.generic') });
    } finally {
      setMoreBusy(false);
    }
  }

  async function onSelect(flightId: string) {
    setBusyId(flightId);
    setRolling(true);
    setMsg(null);
    try {
      const res = await api.game.roll(id, flightId);
      await sleep(750);
      setGstate(res.state);
      if (res.allowed) {
        setSelectedFlight(flights.find((f) => f.id === flightId) ?? null);
        setMsg({ kind: 'win', text: t('game.rollWin') });
      } else {
        setMsg({ kind: 'lose', text: t('game.rollLose') });
      }
    } catch (err) {
      setMsg({ kind: 'error', text: err instanceof ApiError ? t([`errors.${err.code}`, 'errors.generic']) : t('errors.generic') });
    } finally {
      setBusyId(null);
      setRolling(false);
    }
  }

  async function boardFlight() {
    setWaiting(null);
    const res = await api.game.advance(id);
    if (res.state.phase === 'in_flight') {
      navigate(`/round/${id}/live`);
    } else if (res.waitingUntil) {
      setWaiting(res.waitingUntil);
    }
  }

  async function changeMind() {
    try {
      await api.game.change(id);
      setSelectedFlight(null);
      setMsg(null);
      // Tafel des aktuellen Flughafens sicher (neu) laden.
      await load();
    } catch (err) {
      setMsg({ kind: 'error', text: err instanceof ApiError ? t([`errors.${err.code}`, 'errors.generic']) : t('errors.generic') });
    }
  }

  async function finishFree() {
    await api.game.finish(id);
    navigate(`/result/${id}`);
  }

  if (!round || !gstate) return <div className="container page"><div className="spinner" /></div>;

  const airportName = flights[0]?.origin.name ?? selectedFlight?.origin.name ?? gstate.currentAirportIata;
  const originRef = flights[0]?.origin ?? selectedFlight?.origin;
  const mapStart = originRef
    ? { iata: originRef.iata, name: originRef.name, lat: originRef.lat, lon: originRef.lon }
    : null;

  return (
    <div className="container page stack">
      <div className="row">
        <div>
          <div className="tag" data-testid="round-goals">{goalLabel(t, round.goalTypes)}</div>
          <h1 style={{ margin: '2px 0 0' }}>{round.name}</h1>
          <p className="muted mono" style={{ marginTop: 4 }}>
            {gstate.currentAirportIata} · {airportName} · {t('game.gameClock')}: {hhmm(gstate.gameClock)}
          </p>
        </div>
        <div className="spacer" />
        <Link className="btn btn-ghost" to={`/round/${round.id}/settings`} data-testid="round-settings-link">
          {t('settings.link')}
        </Link>
        <ChallengeBadge code={round.challengeCode} />
      </div>

      <div className="grid-2" style={{ alignItems: 'start' }}>
        <div className="stack">
          {gstate.phase === 'boarding' && (
            <>
              <p className="muted">{t('game.boarding')}</p>
              {msg && (
                <div className={`notice ${msg.kind === 'win' ? 'notice-ok' : msg.kind === 'lose' ? 'notice-error' : 'notice-error'}`} data-testid="roll-message">
                  {rolling ? '' : msg.text}
                </div>
              )}
              <p className="muted" style={{ fontSize: '0.82rem', marginTop: -8 }}>{t('game.detailsHint')}</p>
              <DepartureBoard
                airportName={airportName}
                flights={flights}
                rolledIds={gstate.rolledFlightIds}
                onSelect={onSelect}
                onDetails={setDetailFlight}
                busyId={busyId}
                disabled={rolling}
                visibleCount={visibleCount}
                moreMode={moreMode}
                moreBusy={moreBusy}
                onMore={() => void showMore()}
              />
              {round.goalTypes.includes('free') && (
                <button className="btn" onClick={() => void finishFree()} data-testid="finish-free">
                  {t('game.finishFree')}
                </button>
              )}
            </>
          )}

          {gstate.phase === 'selected' && selectedFlight && (
            <div className="card stack" data-testid="selected-flight">
              <div className="eyebrow">{t('game.selectedFlight')}</div>
              <h2 className="mono" style={{ margin: 0 }}>
                {selectedFlight.destination.iata} · {selectedFlight.destination.name}
              </h2>
              <div className="muted mono">
                {selectedFlight.flightNumber} · {selectedFlight.airlineName} · {selectedFlight.aircraftType}
              </div>
              <div className="mono row" style={{ gap: 8, alignItems: 'baseline' }}>
                <FlightTime
                  scheduled={selectedFlight.scheduledDeparture}
                  estimated={selectedFlight.estimatedDeparture}
                  delayMinutes={selectedFlight.delayMinutes}
                />
                <span>→</span>
                <FlightTime
                  scheduled={selectedFlight.scheduledArrival}
                  estimated={selectedFlight.estimatedArrival}
                />
              </div>
              {waiting && (
                <div className="notice" data-testid="live-wait">
                  {t('game.liveWaitLive')} ({hhmm(waiting)})
                </div>
              )}
              <div className="row">
                <button className="btn btn-go" onClick={() => void boardFlight()} data-testid="board-flight">
                  {t('game.board_flight')}
                </button>
                <button className="btn" onClick={() => void changeMind()} data-testid="change-mind">
                  {t('game.change')}
                </button>
                <button className="btn btn-ghost" onClick={() => setDetailFlight(selectedFlight)}>
                  {t('game.details')}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="stack">
          <div className="card stack">
            <div className="eyebrow">{t('game.goal')}</div>
            <p className="muted" style={{ margin: 0, fontSize: '0.88rem' }}>
              {round.goalTypes.length === 1 ? t(`goals.desc.${round.goalTypes[0]}`) : t('game.allGoalsHint')}
            </p>
            <GoalProgress progress={gstate.goalProgress} />
          </div>

          <div className="card stack">
            <div className="eyebrow">{t('game.legs')}</div>
            {gstate.legs.length === 0 ? (
              <p className="muted" style={{ margin: 0 }}>{t('game.noLegs')}</p>
            ) : (
              <ol className="mono" style={{ margin: 0, paddingLeft: 18, fontSize: '0.85rem', lineHeight: 1.7 }}>
                {gstate.legs.map((l) => (
                  <li key={l.order} data-testid="leg-item">
                    {l.from.iata} → {l.to.iata} · {l.flightNumber}
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      </div>

      <RouteMap legs={gstate.legs} start={mapStart} />

      {rolling && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'grid', placeItems: 'center', zIndex: 1000 }}>
          <div className="card center" style={{ padding: 40 }}>
            <div className="dice rolling">🎲</div>
            <p className="mono" style={{ marginBottom: 0 }}>{t('game.rolling')}</p>
          </div>
        </div>
      )}

      {detailFlight && <FlightDetails flight={detailFlight} onClose={() => setDetailFlight(null)} />}
    </div>
  );
}
